import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import clsx from 'clsx';
import { Check, Info, TriangleAlert, X } from 'lucide-react';
import { ApiError } from '../lib/api.js';

type ToastTone = 'info' | 'success' | 'error';
type Toast = { id: number; tone: ToastTone; title: string; body?: string };

type ToastContextValue = {
  toast: (t: { tone?: ToastTone; title: string; body?: string }) => void;
  /** Turn a thrown error into a toast, preferring the server's own wording. */
  toastError: (err: unknown, fallback?: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    ({ tone = 'info', title, body }: { tone?: ToastTone; title: string; body?: string }) => {
      const id = nextId++;
      setItems((prev) => [...prev.slice(-3), { id, tone, title, body }]);
      window.setTimeout(() => remove(id), tone === 'error' ? 9_000 : 5_000);
    },
    [remove],
  );

  const toastError = useCallback(
    (err: unknown, fallback = 'That did not work.') => {
      if (err instanceof ApiError) {
        toast({
          tone: 'error',
          title: err.message,
          // A 403 carries the actionable half of the story in `reason`.
          body: err.reason && err.reason !== err.message ? err.reason : undefined,
        });
        return;
      }
      toast({ tone: 'error', title: fallback, body: (err as Error)?.message });
    },
    [toast],
  );

  const value = useMemo(() => ({ toast, toastError }), [toast, toastError]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-[min(24rem,calc(100vw-2rem))]"
        aria-live="polite"
        aria-atomic="false"
      >
        {items.map((t) => (
          <div
            key={t.id}
            role={t.tone === 'error' ? 'alert' : 'status'}
            className={clsx(
              'flex items-start gap-3 rounded-card border px-4 py-3 shadow-pop bg-surface',
              t.tone === 'error' && 'border-danger/50',
              t.tone === 'success' && 'border-accent/50',
              t.tone === 'info' && 'border-line-strong',
            )}
          >
            <span
              aria-hidden
              className={clsx(
                'mt-0.5 shrink-0',
                t.tone === 'error' && 'text-danger',
                t.tone === 'success' && 'text-ok',
                t.tone === 'info' && 'text-ink-muted',
              )}
            >
              {t.tone === 'error' ? (
                <TriangleAlert size={16} />
              ) : t.tone === 'success' ? (
                <Check size={16} />
              ) : (
                <Info size={16} />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink">{t.title}</p>
              {t.body ? (
                <p className="text-xs text-ink-muted mt-0.5 leading-relaxed">{t.body}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => remove(t.id)}
              aria-label="Dismiss"
              className="text-ink-subtle hover:text-ink shrink-0"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
